import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext, type SessionUser } from "./api";
import { holenMitAblage } from "./ablage";
import { AblageHinweis } from "./netzstand";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf, Laedt, Leer, Seitenkopf, Textfeld } from "./ui";

/**
 * Betriebsanleitungen und Anordnungen.
 *
 * Fuer den Mitarbeiter eine Liste zum Aufklappen, mehr nicht. Kein Blaettern,
 * keine Unterseiten: wer auf einer Leiter steht und wissen will, wie die
 * Winterschliessung geht, soll suchen, tippen, lesen.
 *
 * Die Geschaeftsleitung schreibt in bis zu vier Sprachen. Fehlt eine, faellt
 * der Artikel auf Deutsch zurueck und sagt das auch. Eine Anleitung in der
 * falschen Sprache ist besser als gar keine, aber der Leser soll wissen, dass
 * er die Ersatzfassung sieht.
 */

interface Artikel {
  id: string;
  slug: string;
  titel: string;
  text: string;
  kategorie: string | null;
  sichtbarkeit: string;
  ersatzsprache: boolean;
  geaendert: string;
}

const SPRACHEN = ["de", "en", "es", "ar"] as const;

export function HandbuchSeite({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const istAdmin = user.role === "admin";

  const [artikel, setArtikel] = useState<Artikel[] | null>(null);
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [suche, setSuche] = useState("");
  const [kategorie, setKategorie] = useState("");
  const [offen, setOffen] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [ablageStand, setAblageStand] = useState<string | null>(null);

  const laden = useCallback(async () => {
    const p = new URLSearchParams();
    if (suche.trim()) p.set("suche", suche.trim());
    if (kategorie) p.set("kategorie", kategorie);
    /*
     * Die ungefilterte Liste liegt als Kopie auf dem Geraet. Genau die
     * braucht jemand, der auf einer Leiter am Objekt steht und wissen will,
     * wie die Winterschliessung geht: dort ist oft kein Empfang, und eine
     * Anleitung, die man nur mit Netz lesen kann, ist keine.
     *
     * Gefilterte Abfragen gehen ans Netz. Jede Suchanfrage abzulegen brauchte
     * viel Platz und brächte nichts: ohne Netz steht ohnehin alles da.
     */
    const pfad = `/handbuch${p.toString() ? `?${p}` : ""}`;
    const ungefiltert = !suche.trim() && !kategorie;
    const { daten: a, stand } = ungefiltert
      ? await holenMitAblage<{ artikel: Artikel[]; kategorien: string[] }>(pfad)
      : { daten: await api.get<{ artikel: Artikel[]; kategorien: string[] }>(pfad), stand: null };
    setAblageStand(stand);
    setArtikel(a.artikel);
    // Die Kategorienliste nur setzen, wenn ungefiltert geladen wurde, sonst
    // schrumpft der Filter mit jedem Klick auf sich selbst zusammen.
    if (!kategorie && !suche.trim()) setKategorien(a.kategorien);
  }, [suche, kategorie]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void laden().catch((e) => setFehler(fehlertext(e, "")));
    }, 250);
    return () => window.clearTimeout(t);
  }, [laden]);

  return (
    <>
      <Seitenkopf
        titel={t("handbuch.titel")}
        hinweis={t("handbuch.hinweis")}
        aktionen={istAdmin && <Knopf onClick={() => setNeu(true)}>{t("handbuch.neu")}</Knopf>}
      />
      <Fehler text={fehler} />
      <AblageHinweis stand={ablageStand} />

      <div className="flex flex-wrap items-end gap-3">
        <Feld label={t("handbuch.suche")}>
          <Eingabe value={suche} onChange={(e) => setSuche(e.target.value)} />
        </Feld>
        {kategorien.length > 0 && (
          <Feld label={t("handbuch.kategorie")}>
            <Auswahl value={kategorie} onChange={(e) => setKategorie(e.target.value)}>
              <option value="">{t("handbuch.alleKategorien")}</option>
              {kategorien.map((k) => <option key={k} value={k}>{k}</option>)}
            </Auswahl>
          </Feld>
        )}
      </div>

      {!artikel ? <Laedt /> : artikel.length === 0 ? (
        <Leer text={t("handbuch.leer")} />
      ) : (
        <ul className="mt-4 space-y-2">
          {artikel.map((a) => (
            <li key={a.id}>
              <Karte className="p-4">
                <button
                  className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 text-start"
                  onClick={() => setOffen(offen === a.slug ? null : a.slug)}
                >
                  <span className="font-semibold">{a.titel}</span>
                  {a.kategorie && (
                    <span className="rounded bg-grund px-2 py-0.5 text-xs font-semibold">
                      {a.kategorie}
                    </span>
                  )}
                  {a.sichtbarkeit === "admin" && (
                    <span className="rounded bg-marke/30 px-2 py-0.5 text-xs font-semibold">
                      {t("handbuch.nurChef")}
                    </span>
                  )}
                  {a.ersatzsprache && (
                    <span className="text-xs text-leise">{t("handbuch.ersatzsprache")}</span>
                  )}
                  <span className="ms-auto text-leise">{offen === a.slug ? "−" : "+"}</span>
                </button>

                {offen === a.slug && (
                  <>
                    {/* whitespace-pre-wrap, damit Absaetze und Aufzaehlungen so
                        stehen bleiben, wie sie getippt wurden. Ein Editor mit
                        Formatierung waere hier mehr Werkzeug als Nutzen. */}
                    <p className="mt-3 whitespace-pre-wrap border-t border-linie pt-3 text-sm">
                      {a.text}
                    </p>
                    {istAdmin && (
                      <div className="mt-3 flex gap-2">
                        <Knopf art="neben" onClick={() => setBearbeitet(a.slug)}>
                          {t("aktion.bearbeiten")}
                        </Knopf>
                      </div>
                    )}
                  </>
                )}
              </Karte>
            </li>
          ))}
        </ul>
      )}

      {(neu || bearbeitet) && (
        <ArtikelDialog
          slug={bearbeitet}
          onClose={() => { setNeu(false); setBearbeitet(null); }}
          onFertig={() => { setNeu(false); setBearbeitet(null); void laden(); }}
        />
      )}
    </>
  );
}

function ArtikelDialog({
  slug, onClose, onFertig
}: { slug: string | null; onClose: () => void; onFertig: () => void }) {
  const { t } = useTranslation();
  const [kennung, setKennung] = useState(slug ?? "");
  const [kategorie, setKategorie] = useState("");
  const [sichtbarkeit, setSichtbarkeit] = useState("employee");
  const [titel, setTitel] = useState<Record<string, string>>({});
  const [text, setText] = useState<Record<string, string>>({});
  const [sprache, setSprache] = useState<string>("de");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(!!slug);

  useEffect(() => {
    if (!slug) return;
    void api.get<{
      artikel: Artikel;
      alleSprachen: { titel: Record<string, string>; text: Record<string, string> };
    }>(`/handbuch/${slug}`)
      .then((a) => {
        setTitel(a.alleSprachen.titel);
        setText(a.alleSprachen.text);
        setKategorie(a.artikel.kategorie ?? "");
        setSichtbarkeit(a.artikel.sichtbarkeit);
      })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))))
      .finally(() => setLaeuft(false));
  }, [slug, t]);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      const koerper = {
        slug: kennung,
        titel,
        text,
        kategorie: kategorie || null,
        sichtbarkeit
      };
      if (slug) await api.patch(`/handbuch/${slug}`, koerper);
      else await api.post("/handbuch", koerper);
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  async function archivieren() {
    if (!slug) return;
    setLaeuft(true);
    try {
      await api.del(`/handbuch/${slug}`);
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-full w-full max-w-3xl overflow-y-auto rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">
          {slug ? t("aktion.bearbeiten") : t("handbuch.neu")}
        </h2>
        <Fehler text={fehler} />

        <div className="grid gap-3 sm:grid-cols-3">
          <Feld label={t("handbuch.kennung")} hinweis={t("handbuch.kennungHinweis")}>
            <Eingabe
              value={kennung} disabled={Boolean(slug)}
              onChange={(e) => setKennung(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
            />
          </Feld>
          <Feld label={t("handbuch.kategorie")}>
            <Eingabe value={kategorie} onChange={(e) => setKategorie(e.target.value)} />
          </Feld>
          <Feld label={t("handbuch.sichtbarkeit")}>
            <Auswahl value={sichtbarkeit} onChange={(e) => setSichtbarkeit(e.target.value)}>
              <option value="employee">{t("handbuch.fuerAlle")}</option>
              <option value="admin">{t("handbuch.nurChef")}</option>
            </Auswahl>
          </Feld>
        </div>

        {/* Ein Reiter je Sprache statt vier Feldpaare untereinander. Wer nur
            Deutsch pflegt, sieht auch nur Deutsch. */}
        <div className="mt-5 flex gap-1 border-b border-linie">
          {SPRACHEN.map((l) => (
            <button
              key={l}
              onClick={() => setSprache(l)}
              className={`border-b-2 px-3 py-2 text-sm font-semibold ${
                sprache === l ? "border-marke" : "border-transparent text-leise"
              }`}
            >
              {l.toUpperCase()}
              {titel[l]?.trim() ? "" : " ·"}
            </button>
          ))}
        </div>

        <div className="mt-3 grid gap-3">
          <Feld label={t("handbuch.artikelTitel")}>
            <Eingabe
              value={titel[sprache] ?? ""}
              onChange={(e) => setTitel({ ...titel, [sprache]: e.target.value })}
            />
          </Feld>
          <Feld label={t("handbuch.artikelText")}>
            <Textfeld
              rows={14}
              value={text[sprache] ?? ""}
              onChange={(e) => setText({ ...text, [sprache]: e.target.value })}
            />
          </Feld>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Knopf
            disabled={laeuft || !kennung || !titel.de?.trim() || !text.de?.trim()}
            onClick={() => void speichern()}
          >
            {t("aktion.speichern")}
          </Knopf>
          <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
          {slug && (
            <Knopf art="warnung" disabled={laeuft} onClick={() => void archivieren()}>
              {t("handbuch.archivieren")}
            </Knopf>
          )}
        </div>
        {!titel.de?.trim() && (
          <p className="mt-2 text-sm text-leise">{t("handbuch.deutschPflicht")}</p>
        )}
      </div>
    </div>
  );
}
